/**
 * Azure AD SSO context.
 *
 * - When MSAL is configured (`isAuthEnabled()`), this provider handles the
 *   redirect flow and keeps an `AccountInfo` in state. The backend gets the
 *   user's email + name via `X-User-Email` / `X-User-Name` headers injected
 *   by `api.ts` via `getActiveIdentity()`.
 * - When MSAL is *not* configured (local dev with no Entra app), the
 *   provider serves a deterministic dev identity sourced from
 *   `VITE_DEV_EMAIL` / `VITE_DEV_NAME` so the rest of the app keeps
 *   working without an SSO round-trip.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import type { AccountInfo } from '@azure/msal-browser';

import { getMsalInstance, isAuthEnabled, loginRequest } from './msalConfig';

export interface ActiveIdentity {
  email: string;
  name: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  identity: ActiveIdentity | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  identity: null,
  login: async () => {},
  logout: async () => {},
});

export function useAuth(): AuthContextType {
  return useContext(AuthContext);
}

/**
 * Module-level cache used by the API layer to avoid threading the
 * identity through every `fetch` call. Updated whenever AuthProvider's
 * state changes.
 */
let activeIdentity: ActiveIdentity | null = null;

export function getActiveIdentity(): ActiveIdentity | null {
  return activeIdentity;
}

function accountToIdentity(account: AccountInfo): ActiveIdentity {
  const email = (account.username || '').toLowerCase();
  const name = account.name || email.split('@')[0] || email;
  return { email, name };
}

function devIdentity(): ActiveIdentity {
  const email = (import.meta.env.VITE_DEV_EMAIL || 'dev@local').toLowerCase();
  const name = import.meta.env.VITE_DEV_NAME || email.split('@')[0];
  return { email, name };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [identity, setIdentityState] = useState<ActiveIdentity | null>(null);

  const setIdentity = useCallback((next: ActiveIdentity | null) => {
    activeIdentity = next;
    setIdentityState(next);
  }, []);

  useEffect(() => {
    if (!isAuthEnabled()) {
      setIdentity(devIdentity());
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const msal = await getMsalInstance();
        const response = await msal.handleRedirectPromise();
        if (cancelled) return;
        if (response?.account) {
          setIdentity(accountToIdentity(response.account));
          setIsAuthenticated(true);
          return;
        }
        const accounts = msal.getAllAccounts();
        if (accounts.length > 0) {
          setIdentity(accountToIdentity(accounts[0]));
          setIsAuthenticated(true);
        }
      } catch (e) {
        // Surface to console so the redirect failure is debuggable; the
        // UI just stays on the LoginPage in that case.
        console.error('MSAL init error:', e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setIdentity]);

  const login = useCallback(async () => {
    if (!isAuthEnabled()) return;
    const msal = await getMsalInstance();
    await msal.loginRedirect(loginRequest);
  }, []);

  const logout = useCallback(async () => {
    if (!isAuthEnabled()) {
      setIdentity(null);
      setIsAuthenticated(false);
      return;
    }
    const msal = await getMsalInstance();
    await msal.logoutRedirect();
  }, [setIdentity]);

  const value = useMemo(
    () => ({ isAuthenticated, isLoading, identity, login, logout }),
    [isAuthenticated, isLoading, identity, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
